// src/pages/Bars.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios"; // Keep axios if fetchBars or addBar use it, otherwise remove
import {
    Search,
    MapPin,
    AlertTriangle,
    ListX,
    LogIn,
    Info
} from "lucide-react"; // Import icons

// Constants (assuming these exist and handle tokens/errors)
import fetchBars from "../constants/bars"; // Fetches list of all bars
import addBar from "../constants/addBar"; // API call to associate user with a bar

// Components
// import Sidebar from "../components/Sidebar"; // REMOVE
import Footerbar from "../components/Footerbar"; // ADD
import Navbar from "../components/Navbar"; // ADD
import CustomAlert from "../components/CustomAlert"; // ADD

// --- Helper: Inject CSS Keyframes for Spinners ---
if (!document.getElementById("spinner-keyframes")) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
    const styleSheet = document.createElement("style");
    styleSheet.id = "spinner-keyframes";
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}
// --- End Helper ---

// Helper Function for string normalization (accent removal, lowercase)
const normalize = (str = "") =>
    str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const Bars = () => {
    const [bars, setBars] = useState([]); // Original list of bars (filtered by city or all)
    const [filteredBars, setFilteredBars] = useState([]); // Bars filtered by search query
    const [loading, setLoading] = useState(true); // Main loading state
    const [geoStatus, setGeoStatus] = useState("pending"); // 'pending', 'success', 'error', 'denied', 'unavailable', 'loading_all'
    const [error, setError] = useState(null); // For fetch/geo errors
    const [searchQuery, setSearchQuery] = useState("");
    const [enteringBarId, setEnteringBarId] = useState(null); // Loading state for entering a specific bar
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        message: "",
        type: "error"
    });
    const [selectedCategory, setSelectedCategory] = useState(null); // Categoria selecionada (null = tela de seleção)
    const [hoveredCard, setHoveredCard] = useState(null); // Para efeito hover nos cartões
    const navigate = useNavigate();

    // --- Fetch Location and Bars ---
    useEffect(() => {
        // Não buscar se ainda não selecionou categoria
        if (!selectedCategory) {
            return;
        }

        let isMounted = true;

        const loadAllBars = async (reason = "fallback") => {
            if (!isMounted) return;
            console.log(`BarsScreen: Loading all bars (${reason})...`);
            setGeoStatus(reason === "fallback" ? "loading_all" : geoStatus); // Indicate loading all
            setLoading(true);
            setError(null);
            try {
                const barsData = await fetchBars();
                if (!isMounted) return;
                setBars(barsData || []);
                setFilteredBars(barsData || []);
                setGeoStatus(reason === "fallback" ? "success_all" : geoStatus); // Indicate success loading all
                console.log("BarsScreen: Loaded all bars successfully.");
            } catch (fetchError) {
                console.error("BarsScreen: Error fetching all bars:", fetchError);
                if (isMounted)
                    setError("Erro ao carregar lista de estabelecimentos.");
                setGeoStatus("error"); // Set status to error if fetch fails
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        const getLocationAndBars = async () => {
            if (!navigator.geolocation) {
                console.log("BarsScreen: Geolocation not supported.");
                if (isMounted) setGeoStatus("unavailable");
                await loadAllBars("geolocation_unavailable");
                return;
            }

            if (isMounted) setGeoStatus("pending");
            setLoading(true);
            setError(null);
            console.log("BarsScreen: Requesting geolocation...");

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    if (!isMounted) return;
                    console.log("BarsScreen: Geolocation success.", position.coords);
                    setGeoStatus("success");
                    const { latitude, longitude } = position.coords;

                    try {
                        // Reverse Geocode using Nominatim (consider rate limits)
                        console.log("BarsScreen: Reverse geocoding...");
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&accept-language=pt-BR`
                        ); // Request PT-BR if possible
                        if (!response.ok) {
                            throw new Error(
                                `Nominatim request failed: ${response.statusText}`
                            );
                        }
                        const data = await response.json();
                        console.log("BarsScreen: Geocoding response:", data);
                        // Extract city reliably (city > town > village > county)
                        const cidade =
                            data.address?.city ||
                            data.address?.town ||
                            data.address?.village ||
                            data.address?.county ||
                            null;
                        const estado = data.address?.state || null;

                        console.log(
                            `BarsScreen: Determined location: ${
                                cidade || "Cidade não encontrada"
                            }, ${estado || "Estado não encontrado"}`
                        );

                        // Fetch bars with latitude and longitude for proximity-based results
                        console.log(
                            `BarsScreen: Fetching bars near location (lat: ${latitude}, lon: ${longitude}, category: ${selectedCategory})...`
                        );
                        const barsData = await fetchBars(
                            latitude,
                            longitude,
                            selectedCategory
                        );
                        if (!isMounted) return;

                        // API should return bars sorted by proximity
                        console.log(
                            `BarsScreen: Received ${barsData?.length || 0} bars from API.`
                        );
                        setBars(barsData || []);
                        setFilteredBars(barsData || []);
                    } catch (apiError) {
                        console.error(
                            "BarsScreen: Error during geocoding or bar filtering:",
                            apiError
                        );
                        if (isMounted)
                            setError(
                                "Erro ao processar localização ou buscar bares. Carregando todos."
                            );
                        await loadAllBars("geocoding_or_filter_error"); // Load all if APIs fail after getting coords
                    } finally {
                        if (isMounted) setLoading(false);
                    }
                },
                (geoError) => {
                    // Geolocation permission denied or error
                    if (!isMounted) return;
                    console.error(
                        "BarsScreen: Geolocation error:",
                        geoError.message,
                        `(Code: ${geoError.code})`
                    );
                    if (geoError.code === geoError.PERMISSION_DENIED) {
                        setGeoStatus("denied");
                        setError(
                            "Permissão de localização negada. Mostrando todos os estabelecimentos."
                        );
                    } else {
                        setGeoStatus("error");
                        setError(
                            "Erro ao obter localização. Mostrando todos os estabelecimentos."
                        );
                    }
                    // Fallback: load all bars on geo error/denial
                    loadAllBars("geolocation_error_or_denied");
                },
                { timeout: 10000, enableHighAccuracy: false } // Options: timeout, accuracy
            );
        };

        getLocationAndBars();

        // Cleanup function
        return () => {
            isMounted = false;
            console.log("BarsScreen: Unmounting.");
            // Cleanup any ongoing fetches? (more complex with native fetch/geolocation)
        };
    }, [navigate, selectedCategory]); // Add navigate and selectedCategory to dependencies

    // --- Handle Search Input ---
    const handleSearch = (query) => {
        setSearchQuery(query);
        const lowerCaseQuery = query.toLowerCase().trim();
        if (lowerCaseQuery === "") {
            setFilteredBars(bars); // Show original list (city-filtered or all)
        } else {
            const filtered = bars.filter(
                (bar) =>
                    normalize(bar.name).includes(lowerCaseQuery) || // Search name
                    normalize(bar.address).includes(lowerCaseQuery) // Search address
            );
            setFilteredBars(filtered);
        }
    };

    // --- Handle Entering a Bar ---
    const handleEnterBar = async (bar) => {
        if (enteringBarId) return; // Prevent multiple clicks

        console.log("handleEnterBar initiated for bar:", bar.id, bar.name);
        setEnteringBarId(bar.id); // Set loading state for this bar
        setError(null); // Clear previous errors

        try {
            // Call API constant to associate user with the bar
            await addBar(bar.id); // Assuming addBar handles token and backend logic
            console.log("handleEnterBar: Successfully entered bar:", bar.name);
            // Navigate to home page after successfully entering
            navigate("/home", { replace: true }); // Replace history state so back button doesn't return here
        } catch (err) {
            console.error("handleEnterBar: Error entering bar:", err);
            setError(err.message || "Não foi possível entrar no estabelecimento.");
            setAlertInfo({
                show: true,
                message: err.message || "Não foi possível entrar no estabelecimento.",
                type: "error"
            });
            setEnteringBarId(null); // Reset loading state on error
        }
        // Don't reset enteringBarId on success because we navigate away
    };

    // --- Format Address Helper ---
    const formatAddress = (address, city, state) => {
        let parts = [address, city, state].filter(Boolean); // Filter out null/empty parts
        return parts.join(", ");
    };

    const handleCloseAlert = () =>
        setAlertInfo((prev) => ({ ...prev, show: false }));

    // --- Render ---
    return (
        <div style={styles.pageContainer}>
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    type={alertInfo.type}
                    onclose={handleCloseAlert}
                />
            )}
            <Navbar title="Estabelecimentos" showLogo={false} />

            <div style={styles.contentArea}>
                {/* Tela de Seleção de Categoria */}
                {!selectedCategory && (
                    <div style={styles.categorySelectionContainer}>
                        <h2 style={styles.categoryTitle}>Selecione o tipo de estabelecimento</h2>
                        <p style={styles.categorySubtitle}>
                            Escolha uma categoria para ver os estabelecimentos próximos
                        </p>

                        <div style={styles.categoryGrid}>
                            {/* Cartão: Bares */}
                            <div
                                style={{
                                    ...styles.categoryCard,
                                    ...(hoveredCard === "bar" ? styles.categoryCardHover : {})
                                }}
                                onClick={() => setSelectedCategory("bar")}
                                onMouseEnter={() => setHoveredCard("bar")}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <div style={styles.categoryIcon}>🍺</div>
                                <h3 style={styles.categoryCardTitle}>Bares</h3>
                                <p style={styles.categoryCardDescription}>
                                    Encontre bares próximos de você
                                </p>
                            </div>

                            {/* Cartão: Academias */}
                            <div
                                style={{
                                    ...styles.categoryCard,
                                    ...(hoveredCard === "academia"
                                        ? styles.categoryCardHover
                                        : {})
                                }}
                                onClick={() => setSelectedCategory("academia")}
                                onMouseEnter={() => setHoveredCard("academia")}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <div style={styles.categoryIcon}>💪</div>
                                <h3 style={styles.categoryCardTitle}>Academias</h3>
                                <p style={styles.categoryCardDescription}>
                                    Encontre academias próximas
                                </p>
                            </div>

                            {/* Cartão: Barbearias */}
                            <div
                                style={{
                                    ...styles.categoryCard,
                                    ...(hoveredCard === "barbearia"
                                        ? styles.categoryCardHover
                                        : {})
                                }}
                                onClick={() => setSelectedCategory("barbearia")}
                                onMouseEnter={() => setHoveredCard("barbearia")}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <div style={styles.categoryIcon}>✂️</div>
                                <h3 style={styles.categoryCardTitle}>Barbearias</h3>
                                <p style={styles.categoryCardDescription}>
                                    Encontre barbearias próximas
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Conteúdo Principal (Lista de Estabelecimentos) */}
                {selectedCategory && (
                    <>
                        {/* Search Input */}
                        <div style={styles.searchContainer}>
                            <Search
                                size={20}
                                color={styles.searchInputIcon.color}
                                style={styles.searchInputIcon}
                            />
                            <input
                                type="text"
                                style={styles.searchInput}
                                placeholder="Buscar por nome ou endereço..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>

                        {/* Loading State */}
                        {loading && (
                            <div style={styles.centeredMessage}>
                                <div style={styles.spinner}></div>
                                <p>
                                    {geoStatus === "pending"
                                        ? "Obtendo localização..."
                                        : "Carregando estabelecimentos..."}
                                </p>
                            </div>
                        )}

                        {/* Error/Status Messages */}
                        {!loading &&
                            (geoStatus === "error" ||
                                geoStatus === "denied" ||
                                geoStatus === "unavailable") && (
                                <div style={styles.centeredMessage}>
                                    <AlertTriangle size={30} color="#ffcc00" />
                                    <p
                                        style={{
                                            color: "#ffcc00",
                                            marginTop: "10px",
                                            maxWidth: "80%"
                                        }}
                                    >
                                        {error ||
                                            (geoStatus === "denied"
                                                ? "Permissão de localização negada."
                                                : "Não foi possível obter sua localização.")}
                                    </p>
                                    <p style={{ fontSize: "14px", marginTop: "5px" }}>
                                        {geoStatus !== "success_all"
                                            ? "Mostrando todos os estabelecimentos disponíveis."
                                            : ""}
                                    </p>
                                </div>
                            )}

                        {/* Message when successfully loaded all bars as fallback */}
                        {!loading && geoStatus === "success_all" && !error && (
                            <div style={styles.infoMessage}>
                                <Info
                                    size={18}
                                    style={{ marginRight: "8px", flexShrink: 0 }}
                                />
                                <span>
                  Não foi possível filtrar por cidade. Mostrando todos os
                  estabelecimentos.
                </span>
                            </div>
                        )}

                        {/* Empty State (No Bars Found) */}
                        {!loading && !error && filteredBars.length === 0 && (
                            <div style={styles.centeredMessage}>
                                <ListX size={40} color="#a0a0b0" />
                                <p style={{ color: "#a0a0b0", marginTop: "10px" }}>
                                    {searchQuery
                                        ? `Nenhum estabelecimento encontrado para "${searchQuery}".`
                                        : "Nenhum estabelecimento encontrado."}
                                </p>
                                {searchQuery && (
                                    <p style={{ color: "#777", fontSize: "14px" }}>
                                        Verifique o termo buscado ou limpe a busca.
                                    </p>
                                )}
                                {!searchQuery &&
                                    (geoStatus === "success" ||
                                        geoStatus === "denied" ||
                                        geoStatus === "error" ||
                                        geoStatus === "unavailable") && (
                                        <p
                                            style={{
                                                color: "#777",
                                                fontSize: "14px",
                                                marginTop: "5px"
                                            }}
                                        >
                                            {geoStatus === "success"
                                                ? "Não há bares cadastrados na sua cidade."
                                                : "Tente buscar manualmente."}
                                        </p>
                                    )}
                            </div>
                        )}

                        {/* Bars Grid/List */}
                        {!loading && !error && filteredBars.length > 0 && (
                            <div style={styles.barsGrid}>
                                {filteredBars.map((bar) => (
                                    <div key={bar.id} style={styles.barCard}>
                                        <img
                                            src={
                                                bar.logo ||
                                                "https://via.placeholder.com/100?text=Logo"
                                            } // Placeholder
                                            alt={`Logo ${bar.name}`}
                                            style={styles.logo}
                                            onError={(e) => {
                                                e.target.src =
                                                    "https://via.placeholder.com/100?text=Logo";
                                            }} // Handle broken image
                                        />
                                        <h3 style={styles.barName}>{bar.name}</h3>
                                        <p style={styles.barAddress}>
                                            <MapPin
                                                size={12}
                                                style={{ marginRight: "4px", opacity: 0.8 }}
                                            />
                                            {formatAddress(bar.address, bar.city, bar.state)}
                                        </p>
                                        <button
                                            style={styles.enterButton(enteringBarId === bar.id)}
                                            onClick={() => handleEnterBar(bar)}
                                            disabled={!!enteringBarId} // Disable if *any* bar is being entered
                                        >
                                            {enteringBarId === bar.id ? (
                                                <>
                                                    <div style={styles.buttonSpinner}></div> Entrando...
                                                </>
                                            ) : (
                                                <>
                                                    <LogIn size={16} /> Entrar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <Footerbar />
        </div>
    );
};

// --- Styles ---
const styles = {
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#12121f',
        color: '#f0f0f0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    contentArea: {
        flexGrow: 1,
        paddingTop: '70px', // Space below Navbar
        paddingBottom: '75px', // Space above Footerbar
        paddingLeft: '10px',
        paddingRight: '10px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex', // Needed for flexGrow and potential vertical centering of messages
        flexDirection: 'column', // Stack search and grid/messages
        alignItems: 'center', // Center search bar and grid container
    },
    categorySelectionContainer: {
        width: '100%',
        maxWidth: '800px',
        padding: '20px',
        textAlign: 'center',
    },
    categoryTitle: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#f0f0f0',
        marginBottom: '10px',
    },
    categorySubtitle: {
        fontSize: '16px',
        color: '#a0a0b0',
        marginBottom: '30px',
    },
    categoryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        width: '100%',
    },
    categoryCard: {
        backgroundColor: '#1a1a2e',
        borderRadius: '15px',
        padding: '30px 20px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid transparent',
    },
    categoryCardHover: {
        transform: 'translateY(-5px)',
        borderColor: '#6a0dad',
        boxShadow: '0 8px 20px rgba(106, 13, 173, 0.3)',
    },
    categoryIcon: {
        fontSize: '60px',
        marginBottom: '15px',
    },
    categoryCardTitle: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#f0f0f0',
        marginBottom: '10px',
    },
    categoryCardDescription: {
        fontSize: '14px',
        color: '#a0a0b0',
    },
    searchContainer: {
        position: 'relative',
        marginBottom: '20px',
        width: '100%',
        maxWidth: '600px', // Limit search bar width
    },
    searchInput: {
        width: "100%",
        backgroundColor: "#1a1a2e",
        padding: "12px 15px 12px 40px", // Left padding for icon
        borderRadius: "8px",
        fontSize: "14px",
        border: "1px solid #4a4a6e",
        color: '#f0f0f0',
        outline: 'none',
        boxSizing: 'border-box',
    },
    searchInputIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#a0a0b0',
        pointerEvents: 'none',
    },
    centeredMessage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '30px 20px', // Reduced padding
        color: '#a0a0b0',
        flexGrow: 1, // Allow message area to take space if list is empty
        width: '100%',
    },
    infoMessage: { // Specific style for non-error info messages
        width: 'calc(100% - 20px)', // Take full width minus padding
        maxWidth: '600px',
        backgroundColor: 'rgba(160, 160, 255, 0.1)', // Light purple background
        border: '1px solid rgba(160, 160, 255, 0.3)',
        color: '#c0c0ff', // Lighter purple text
        padding: '10px 15px',
        borderRadius: '8px',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        marginBottom: '15px', // Space below info message
        boxSizing: 'border-box',
    },
    errorText: {
        color: '#ffcc00', // Warning color for geo errors/denial
        marginTop: '10px',
        maxWidth: '90%',
        lineHeight: '1.4',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
    },
    spinner: {
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#a0a0ff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        animation: 'spin 1s linear infinite',
        marginBottom: '15px',
    },
    barsGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px', // Gap between cards
        justifyContent: 'center', // Center cards within the line
        width: '100%',
        maxWidth: '1000px', // Max width for the grid container itself
        paddingBottom: '10px', // Padding at the bottom of the grid
    },
    barCard: {
        backgroundColor: "#1a1a2e",
        borderRadius: "10px",
        padding: "15px", // Increased padding
        display: 'flex',
        flexDirection: 'column', // Stack content vertically
        alignItems: "center", // Center content horizontally
        boxShadow: "0 3px 8px rgba(0, 0, 0, 0.4)",
        textAlign: "center",
        border: '1px solid #2a2a4e',
        boxSizing: 'border-box',
        // --- Grid Item Sizing ---
        // For ~2 columns on mobile, more on wider screens
        width: 'calc(50% - 8px)', // 50% minus half the gap
        '@media (min-width: 600px)': { // Example media query (needs CSS solution)
            width: 'calc(33.33% - 10px)', // ~3 columns
        },
        '@media (min-width: 900px)': {
            width: 'calc(25% - 12px)', // ~4 columns
        },
        // --- Fallback without media queries (less ideal) ---
        flex: '1 1 150px', // Allow items to grow/shrink from a base of 150px
        maxWidth: 'calc(50% - 8px)', // Limit max width for 2-column feel mostly

    },
    logo: {
        width: "80px", // Slightly smaller logo
        height: "80px",
        borderRadius: "8px", // Less rounded
        marginBottom: "12px",
        objectFit: "contain", // Use contain to ensure logo isn't cut off
        backgroundColor: '#fff', // White background for logos with transparency
    },
    barName: {
        fontSize: "15px", // Slightly smaller name
        fontWeight: "bold",
        color: '#ffffff',
        marginBottom: "5px",
        width: '100%', // Ensure text wrapping works
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
    },
    barAddress: {
        fontSize: "12px",
        color: "#a0a0b0", // Lighter address text
        marginBottom: "12px", // Space before button
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center', // Center address line
        width: '100%',
        lineHeight: '1.3',
        // Limit address to 2 lines maybe?
        // display: '-webkit-box',
        // WebkitLineClamp: 2,
        // WebkitBoxOrient: 'vertical',
        // overflow: 'hidden',
        // textOverflow: 'ellipsis',
    },
    enterButton: (isLoading) => ({
        // Using buttonPrimary styles from EditProfile for consistency
        width: "100%",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px', // Adjusted gap
        padding: "10px", // Slightly smaller padding
        borderRadius: "8px",
        fontSize: "14px", // Adjusted font size
        fontWeight: 'bold',
        border: "none",
        cursor: isLoading ? "not-allowed" : "pointer",
        transition: 'background-color 0.3s ease, opacity 0.3s ease',
        boxSizing: 'border-box',
        marginTop: 'auto', // Push button to bottom if card heights vary
        // Primary action color
        backgroundColor: isLoading ? '#5a0ca0' : '#6a0dad',
        color: "white",
        opacity: isLoading ? 0.7 : 1,
    }),
    buttonSpinner: {
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#fff',
        borderRadius: '50%',
        width: '14px', // Smaller spinner
        height: '14px',
        animation: 'spin 1s linear infinite',
        display: 'inline-block',
    },

}; // --- End Styles ---
export default Bars; // Ensure default export is present
